import { useAtomValue } from "jotai";
import type { PageProps } from "next";
import React, { useEffect } from "react";
import { graphql, usePreloadedQuery } from "react-relay";
import type { RelayComponent } from "relay-nextjs";
import { withRelay } from "relay-nextjs";
import { isString } from "scmp-sdk";

import { notEmpty } from "shared/lib/utils";

import { FallbackLoading } from "scmp-app/components/fallback-loading";
import { PlusMobileScreen } from "scmp-app/components/plus/mobile-screen";
import { PlusNewsAgenda } from "scmp-app/components/plus/news-agenda";
import { PlusHomeSeo } from "scmp-app/components/plus/plus-home-seo";
import { trackingAtom } from "scmp-app/components/tracking";
import { createClientEnvironment } from "scmp-app/lib/relay/environment.client";
import { useGetPageLocationForPlusPage } from "scmp-app/pages/plus/hooks";
import { plusGlobalCss } from "scmp-app/pages/plus/styles";
import type { agendaPlusNewsAgendaPageQuery } from "scmp-app/queries/__generated__/agendaPlusNewsAgendaPageQuery.graphql";

const query = graphql`
  query agendaPlusNewsAgendaPageQuery($nodeId: ID!, $hasNodeId: Boolean!) {
    node(id: $nodeId) @include(if: $hasNodeId) {
      ...plusHomeSeoNode
    }
  }
`;

const PlusNewsAgendaPage: RelayComponent<PageProps, agendaPlusNewsAgendaPageQuery> = ({
  preloadedQuery,
}) => {
  const data = usePreloadedQuery(query, preloadedQuery);

  const pageLocation = useGetPageLocationForPlusPage();
  const { trackPageView } = useAtomValue(trackingAtom);

  useEffect(() => {
    if (!notEmpty(pageLocation)) {
      return;
    }

    trackPageView?.();
  }, [pageLocation, trackPageView]);
  return (
    <>
      <PlusHomeSeo reference={data.node} />
      <PlusMobileScreen />
      <PlusNewsAgenda />
    </>
  );
};

PlusNewsAgendaPage.displayName = "PlusNewsAgendaPage";

const pageProps: PageProps = {
  animateKey: "plus-agenda",
  appBarConfiguration: {
    variant: "scmp/plus",
  },
  appInitializerConfiguration: {
    application: "plus",
  },
  extraGlobalCss: plusGlobalCss,
};

export default withRelay(PlusNewsAgendaPage, query, {
  clientSideProps: () => pageProps,
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("scmp-app/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: () => Promise.resolve(pageProps),
  variablesFromContext(context) {
    return {
      hasNodeId: notEmpty(context.query?.nodeId),
      nodeId: isString(context.query.nodeId) ? (context.query?.nodeId ?? "") : "",
    };
  },
});
